import { Skeleton, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { budgetFormat } from "../../../utils/number/budget-format.ts"
import { KpiExplanation, emptyStateText } from "../../../components/widgets/kpi/KpiWidget"
import type { KpiTile } from "../../../components/widgets/types"
import { TABULAR, homeColor, homeText } from "../home-theme.ts"
import {
  ClipboardCheckIcon,
  ClockIcon,
  DollarSignIcon,
  FolderIcon,
  GaugeIcon,
  IconTile,
  InfoIcon,
  ReceiptTextIcon,
  TrendingUpIcon,
  TrophyIcon
} from "./home-icons.tsx"

type Tint = { bg: string; fg: string }

const TEAL: Tint = { bg: homeColor.tealPale, fg: homeColor.teal }
const AMBER: Tint = { bg: homeColor.amberBg, fg: homeColor.amber }
const RED: Tint = { bg: homeColor.redBg, fg: homeColor.red }
const GREEN: Tint = { bg: homeColor.greenBg, fg: homeColor.green }

type Glyph = React.FC<React.SVGProps<SVGSVGElement>>

/**
 * The reference's own icon per metric. Falls back by the catalog's `icon`
 * key so a tile the backend adds later still gets a sensible glyph.
 */
const BY_METRIC: Record<string, { icon: Glyph; tint: Tint }> = {
  active_projects: { icon: FolderIcon, tint: TEAL },
  delayed_projects: { icon: ClockIcon, tint: RED },
  pending_approvals: { icon: ClipboardCheckIcon, tint: TEAL },
  budget_health: { icon: GaugeIcon, tint: AMBER },
  revenue: { icon: DollarSignIcon, tint: GREEN },
  revenue_in_window: { icon: DollarSignIcon, tint: GREEN },
  outstanding_receivables: { icon: ReceiptTextIcon, tint: TEAL },
  gross_margin: { icon: TrendingUpIcon, tint: TEAL },
  win_rate: { icon: TrophyIcon, tint: TEAL }
}

const BY_ICON_KEY: Record<string, Glyph> = {
  briefcase: FolderIcon,
  clock: ClockIcon,
  check: ClipboardCheckIcon,
  gauge: GaugeIcon,
  currency: DollarSignIcon,
  receipt: ReceiptTextIcon,
  wallet: ReceiptTextIcon,
  trending: TrendingUpIcon,
  trophy: TrophyIcon,
  target: TrophyIcon
}

export function tileGlyph(tile: Pick<KpiTile, "id"> & { icon?: string }) {
  const byMetric = BY_METRIC[tile.id]
  if (byMetric) return byMetric
  const byKey = tile.icon ? BY_ICON_KEY[tile.icon] : undefined
  return { icon: byKey ?? TrendingUpIcon, tint: TEAL }
}

const SCALE_MAX = 100

/** Percent tiles are the only ones whose range is knowable client-side. */
function percentScale(tile: KpiTile): number | null {
  if (tile.unit !== "percent") return null
  const n = Number(tile.value)
  if (!Number.isFinite(n)) return null
  return Math.max(0, Math.min(SCALE_MAX, n))
}

const format = (tile: KpiTile, currency: string): string => {
  const n = Number(tile.value)
  if (!Number.isFinite(n)) return `${tile.value}`
  if (tile.unit === "percent") return `${n.toFixed(1)}%`
  if (tile.unit === "currency") return budgetFormat(n, { currency })
  return `${tile.value}`
}

export const HomeMetricTileSkeleton = () => (
  <div style={{ padding: "20px 22px" }}>
    <Skeleton.Node active style={{ width: "100%", height: 52 }} />
  </div>
)

/**
 * One cell of the unified Portfolio Overview grid — no card of its own, which
 * is the whole point: eight outlined boxes inside an outlined section is what
 * made the previous pass read as monotonous.
 */
export const HomeMetricTile = ({
  tile,
  currency = "USD"
}: {
  tile: KpiTile
  currency?: string
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { icon, tint } = tileGlyph(tile)
  const noData = tile.has_data === false
  const scale = percentScale(tile)
  const clickable = Boolean(tile.drill_to)
  const portfolioWide = Boolean(tile.filters_ignored?.length)
  const hasExplanation = Boolean(tile.description || tile.formula || portfolioWide)

  return (
    <div
      data-testid="kpi-card"
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => navigate(tile.drill_to) : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                navigate(tile.drill_to)
              }
            }
          : undefined
      }
      style={{
        display: "flex",
        gap: 14,
        alignItems: "center",
        padding: "20px 22px",
        minWidth: 0,
        cursor: clickable ? "pointer" : "default"
      }}
    >
      <IconTile
        icon={icon}
        size={48}
        iconSize={24}
        background={tint.bg}
        color={tint.fg}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            ...homeText.metricLabel,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: homeColor.inkMutedStrong
          }}
        >
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {tile.title}
          </span>
          {hasExplanation ? (
            <Tooltip
              title={<KpiExplanation tile={tile} />}
              placement="top"
              trigger={["hover", "focus"]}
            >
              <button
                type="button"
                data-testid="kpi-info"
                aria-label={t("portfolio.tile.infoLabel", "How this is calculated")}
                onClick={(event) => event.stopPropagation()}
                style={{
                  display: "inline-flex",
                  flex: "none",
                  cursor: "help",
                  padding: 0,
                  border: "none",
                  background: "none",
                  color: homeColor.inkMuted,
                  lineHeight: 0
                }}
              >
                <InfoIcon width={13} height={13} />
              </button>
            </Tooltip>
          ) : null}
        </span>

        {noData ? (
          <span style={{ ...homeText.body, color: homeColor.inkMutedStrong }}>
            {emptyStateText(tile, t)}
          </span>
        ) : (
          <span
            data-testid="kpi-value"
            style={{
              ...homeText.metricValue,
              ...TABULAR,
              color: homeColor.ink,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {format(tile, currency)}
          </span>
        )}

        {!noData && scale !== null ? (
          <div
            data-testid="kpi-scale"
            data-scale-value={scale}
            aria-hidden
            style={{
              height: 6,
              borderRadius: 999,
              background: homeColor.progressTrack,
              overflow: "hidden"
            }}
          >
            <div
              style={{
                width: `${scale}%`,
                height: "100%",
                borderRadius: 999,
                // Budget Health is the one scale whose low end is a warning,
                // and the reference colours it so.
                background: tint.fg === homeColor.amber ? homeColor.amber : homeColor.teal
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
